/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useState } from "react";
import Select from "react-select";
import {
  Draggable,
  ConfirmModal,
  TransformProps,
  DEFAULT_TIMEZONE,
  REGEX_PATH,
} from "@advapacs/common";
import { useTranslation } from "react-i18next";
import {
  REMOVE,
  CONSTANT,
  REGEX,
  COPY,
  SCRIPT,
  DICOM,
  DAY_OF_WEEK,
  TIME_OF_DAY,
  ANONYMIZE,
  DYNAMIC,
  AI_RELEVANCY,
  getSelectStyles,
} from "../../constant";
import Dicom from "../Rules/Fields/Dicom";
import Dynamic from "../Rules/Fields/Dynamic";
import Day from "../Rules/Fields/Day";
import Time from "../Rules/Fields/Time";
import Remove from "./Fields/Remove";
import Constant from "./Fields/Constant";
import Regex from "./Fields/Regex";
import Copy from "./Fields/Copy";
import Script from "./Fields/Script";
import Anonymize from "./Fields/Anonymize";
import RegexPath from "./Fields/RegexPath";
import { EXTERNAL_ERROR_FIELDS } from "./Fields/ExternalError";

const Transform: FunctionComponent<TransformProps> = ({
  ruleTypes,
  usedTypes,
  disabled,
  onRemoveTransform,
  id,
  errors,
  transform,
  type,
  onValueChangeHandler,
  transformType,
  titleConfirmRemove,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isDynamicConditionType = false,
  isDark = false,
  onHandleScriptModal,
}: TransformProps) => {
  const { t } = useTranslation(["rule"]);
  const [canDrag, setCanDrag] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [localType, setLocalType] = useState(type);

  // Event Handlers for Condition
  const deleteRule = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleting(true);
  };

  const updateType = (newType: any) => {
    setLocalType(newType.value);
    const data: any = {
      type: newType.value,
      id,
    };
    if (newType.value === CONSTANT)
      data.transform = {
        value: "",
      };
    if (newType.value === AI_RELEVANCY) data.aiRelevancy = {};
    onValueChangeHandler(data);
  };

  const getTypeOptions = () => {
    return ruleTypes
      .filter(
        (option) =>
          !(option === AI_RELEVANCY && usedTypes?.includes(AI_RELEVANCY))
      )
      .map((option) => {
        return {
          label: t(option.toLowerCase()).toUpperCase(),
          value: option,
        };
      });
  };

  // backend errors not tied to an input this transform type renders are shown under the whole transform
  const unplacedErrors = errors.filter(
    (item) =>
      item.id === id &&
      item.message &&
      !(item.field && EXTERNAL_ERROR_FIELDS[localType]?.includes(item.field))
  );

  const confirmRemove = () => {
    setDeleting(false);
    onRemoveTransform(id);
  };

  const cancelRemove = () => {
    setDeleting(false);
  };

  return (
    <>
      <Draggable
        className="app-sla-block-container drag-element"
        disableDrag={!canDrag || disabled}
        dataItem={JSON.stringify({ value: transform })}
      >
        {}
        <ConfirmModal
          show={deleting}
          onClose={cancelRemove}
          body={titleConfirmRemove || t("confirm_transform_removal")}
          cancel={cancelRemove}
          confirm={confirmRemove}
        />
        <div className="transform">
          <div className="row p-2 ml-0 mr-0 mt-1 mb-0">
            <div className="col-md-3 col-sm-4 mb-1">
              <div className="d-inline-block" style={{ width: "100%" }}>
                <Select
                  menuPlacement="auto"
                  menuPosition="fixed"
                  styles={getSelectStyles(isDark)}
                  id={`${id}_type`}
                  isDisabled={disabled}
                  options={getTypeOptions()}
                  value={getTypeOptions().find(
                    (option) => option?.value === transform?.type
                  )}
                  placeholder={t("select_type")}
                  onChange={updateType}
                />
                {errors.find((item) => item.id === id) !== undefined &&
                  !transform?.type && (
                    <label
                      className="validation-invalid-label"
                      htmlFor={`${id}_type`}
                    >
                      {t("field_required")}
                    </label>
                  )}
              </div>
            </div>
            <div className="col-md-8 col-sm-6">
              <div>
                {localType === REMOVE && (
                  <Remove
                    disabled={disabled}
                    errors={errors}
                    isDark={isDark}
                    id={id}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}
                {localType === CONSTANT && (
                  <Constant
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    isDark={isDark}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}
                {localType === REGEX && (
                  <Regex
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    isDark={isDark}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}

                {localType === REGEX_PATH && (
                  <RegexPath
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    isDark={isDark}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}

                {localType === COPY && (
                  <Copy
                    disabled={disabled}
                    isDark={isDark}
                    errors={errors}
                    id={id}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}
                {localType === SCRIPT && (
                  <Script
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    isDark={isDark}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                    onHandleScriptModal={onHandleScriptModal}
                  />
                )}
                {localType === DICOM && (
                  <Dicom
                    isDark={isDark}
                    isHasScope={false}
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    parent={transform.id}
                    condition={{
                      ...transform?.dicom,
                      caseSensitive:
                        transform?.dicom?.caseSensitive === undefined
                          ? true
                          : transform?.dicom?.caseSensitive,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
                {localType === DAY_OF_WEEK && (
                  <Day
                    isDark={isDark}
                    isHasScope={false}
                    disabled={disabled}
                    id={id}
                    errors={errors}
                    parent={transform.id}
                    condition={transform?.dayOfWeek}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
                {localType === TIME_OF_DAY && (
                  <Time
                    isHasScope={false}
                    disabled={disabled}
                    id={id}
                    errors={errors}
                    isDark={isDark}
                    parent={transform.id}
                    condition={transform?.timeOfDay}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === ANONYMIZE && (
                  <Anonymize
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    transform={transform?.transform}
                    onValueChangeHandler={onValueChangeHandler}
                    transformType={transformType}
                  />
                )}

                {localType === DYNAMIC && (
                  <Dynamic
                    isFromTransform={isDynamicConditionType}
                    disabled={disabled}
                    errors={errors}
                    isDark={isDark}
                    id={id}
                    parent={transform.id}
                    condition={{
                      ...transform?.dynamic,
                      timeZone:
                        transform?.dynamic?.timeZone || DEFAULT_TIMEZONE.value,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
              </div>
            </div>
            <div className="col-md-1 col-sm-2 text-right">
              {!disabled && (
                <div
                  role="button"
                  tabIndex={0}
                  onMouseOver={() => {
                    setCanDrag(true);
                  }}
                  onFocus={() => {
                    setCanDrag(true);
                  }}
                  onMouseLeave={() => {
                    setCanDrag(false);
                  }}
                  className="btn btn-sm drag-group d-inline"
                  style={{ paddingLeft: "0", paddingRight: "0" }}
                >
                  <i className="icon-move" />
                </div>
              )}
              {!disabled && (
                <button
                  onClick={deleteRule}
                  type="button"
                  className="btn btn-sm d-inline text-danger"
                  style={{ paddingLeft: "0", paddingRight: "0" }}
                >
                  <i className="icon-trash" title={t("general.delete")} />
                </button>
              )}
            </div>
          </div>
          {unplacedErrors.map((item) => (
            <label
              key={item.message}
              className="validation-invalid-label px-3 pb-2 mt-0"
            >
              {item.message}
            </label>
          ))}
        </div>
      </Draggable>
    </>
  );
};

export default Transform;
